<template>
	<div
		class="rc"
		:class="{ 'rc--drag': trascinamento }"
		data-tour="reply"
		@dragenter.prevent="trascinamento = true"
		@dragover.prevent="trascinamento = true"
		@dragleave.prevent="fuoriDalRiquadro"
		@drop.prevent="rilascia"
	>
		<div class="rc-head">
			<v-icon small class="mr-1">mdi-reply</v-icon>
			<span class="rc-head-title">Rispondi a {{ destinatario || 'cliente' }}</span>
			<span v-if="email" class="rc-head-email">· {{ email }}</span>
		</div>

		<div class="rc-body">
			<v-text-field v-model="oggetto" label="Oggetto" dense outlined hide-details class="mb-3" :disabled="invio"></v-text-field>

			<div class="rc-editor">
				<ckeditor v-model="testo" :editor="editor" :config="editorConfig" :disabled="invio" />
			</div>

			<div v-if="allegati.length" class="rc-attachs">
				<v-chip v-for="(a, i) in allegati" :key="a.id" small outlined :close="!a.caricamento && !invio" class="rc-chip" :color="a.errore ? 'error' : undefined" @click:close="togli(i)">
					<v-progress-circular v-if="a.caricamento" indeterminate size="14" width="2" color="primary" class="mr-2"></v-progress-circular>
					<img v-else-if="a.url && eImmagine(a.nome)" :src="a.url" alt="" class="rc-chip-img" />
					<v-icon v-else left small>{{ a.errore ? 'mdi-alert-circle-outline' : 'mdi-paperclip' }}</v-icon>
					<span class="text-truncate rc-chip-name" :title="a.errore || a.nome">{{ a.nome }}</span>
				</v-chip>
			</div>
			<div v-if="avviso" class="rc-warning"><v-icon x-small color="error" class="mr-1">mdi-alert-circle-outline</v-icon>{{ avviso }}</div>

			<div class="rc-actions">
				<v-btn small text color="primary" :disabled="invio" @click="$refs.file.click()"><v-icon left small>mdi-paperclip</v-icon>Allega file</v-btn>
				<input ref="file" type="file" multiple class="d-none" :accept="ACCETTATI.map(e => '.' + e).join(',')" @change="scelti" />
				<span class="rc-hint">PDF, Word, Excel, immagini, ZIP/RAR · max {{ MAX_MB }} MB · o trascinali qui</span>
				<v-spacer></v-spacer>
				<v-btn small text :disabled="invio || vuoto" @click="svuota">Svuota</v-btn>
				<v-btn small depressed color="primary" :loading="invio" :disabled="!pronto" @click="invia"><v-icon left small>mdi-send</v-icon>Invia risposta</v-btn>
			</div>
		</div>
	</div>
</template>

<script>
import axios from 'axios';
import { uuid } from 'vue-uuid';
import CKEditor from '@ckeditor/ckeditor5-vue2';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { testoDaEntita } from '@/utils/emailHtml';

const ACCETTATI = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'zip', 'rar', 'jpg', 'jpeg', 'png', 'gif'];
const MAX_MB = 20;
const IMMAGINI = ['jpg', 'jpeg', 'png', 'gif'];

// "R: " una sola volta, anche se l'ultimo oggetto era gia' una risposta o un inoltro
const oggettoRisposta = s => {
	const base = testoDaEntita(String(s || ''))
		.replace(/^(\s*(r|re|rif|fw|fwd|i|inoltra)\s*:\s*)+/i, '')
		.trim();
	return base ? `R: ${base}` : '';
};

// modulo di risposta sotto la conversazione: il salvataggio/invio lo fa il genitore (evento `send`)
export default {
	name: 'ReplyComposer',
	components: { ckeditor: CKEditor.component },
	props: {
		destinatario: { type: String, default: '' },
		email: { type: String, default: '' },
		// oggetto dell'ultimo messaggio: la risposta parte da "R: ..."
		ultimoOggetto: { type: String, default: '' },
		// il genitore lo mette a true durante la chiamata di invio
		invio: { type: Boolean, default: false },
	},
	data() {
		return {
			ACCETTATI,
			MAX_MB,
			oggetto: oggettoRisposta(this.ultimoOggetto),
			oggettoToccato: false,
			testo: '',
			allegati: [],
			avviso: '',
			trascinamento: false,
			uuid: uuid.v4(),
			editor: ClassicEditor,
			editorConfig: {
				toolbar: ['bold', 'italic', 'link', '|', 'bulletedList', 'numberedList', '|', 'undo', 'redo'],
				placeholder: 'Scrivi la risposta al cliente…',
			},
		};
	},
	computed: {
		testoPieno() {
			return !!String(this.testo || '')
				.replace(/<[^>]*>/g, '')
				.replace(/&nbsp;/g, ' ')
				.trim();
		},
		vuoto() {
			return !this.testoPieno && !this.allegati.length;
		},
		pronto() {
			return !this.invio && !!this.oggetto.trim() && this.testoPieno && !this.allegati.some(a => a.caricamento);
		},
	},
	watch: {
		// nuova conversazione (o nuovo messaggio arrivato): si propone l'oggetto finche' non e' stato cambiato a mano
		ultimoOggetto(v) {
			if (!this.oggettoToccato) this.oggetto = oggettoRisposta(v);
		},
		oggetto(v) {
			if (v !== oggettoRisposta(this.ultimoOggetto)) this.oggettoToccato = true;
		},
	},
	methods: {
		estensione(nome) {
			return String(nome || '')
				.split('.')
				.pop()
				.toLowerCase();
		},
		eImmagine(nome) {
			return IMMAGINI.includes(this.estensione(nome));
		},
		scelti(e) {
			this.carica(Array.from(e.target.files || []));
			e.target.value = '';
		},
		rilascia(e) {
			this.trascinamento = false;
			if (!this.invio) this.carica(Array.from((e.dataTransfer && e.dataTransfer.files) || []));
		},
		fuoriDalRiquadro(e) {
			if (!this.$el.contains(e.relatedTarget)) this.trascinamento = false;
		},
		carica(files) {
			this.avviso = '';
			const scartati = [];
			files.forEach(file => {
				if (!ACCETTATI.includes(this.estensione(file.name))) return scartati.push(`${file.name} (formato non ammesso)`);
				if (file.size > MAX_MB * 1024 * 1024) return scartati.push(`${file.name} (oltre ${MAX_MB} MB)`);
				this.caricaUno(file);
				return null;
			});
			if (scartati.length) this.avviso = `Non allegati: ${scartati.join(', ')}`;
		},
		async caricaUno(file) {
			const voce = { id: `${Date.now()}-${Math.random()}`, nome: file.name, url: '', caricamento: true, errore: '' };
			this.allegati.push(voce);
			const dati = new FormData();
			dati.append('uuid', this.uuid);
			dati.append('type', 'file');
			dati.append('folder', 'email');
			dati.append('file', file, file.name);
			try {
				const { data } = await axios.post(`${this.$store.getters['settings/getUrl']}multimedias/uploadfile`, dati, {
					headers: { Authorization: `Bearer ${this.$store.getters['profile/getToken']}` },
				});
				voce.url = data.url;
			} catch (e) {
				voce.errore = 'Caricamento non riuscito';
			} finally {
				voce.caricamento = false;
			}
		},
		togli(i) {
			this.allegati.splice(i, 1);
		},
		svuota() {
			this.testo = '';
			this.allegati = [];
			this.avviso = '';
		},
		// dopo un invio riuscito (chiamato dal genitore)
		reset() {
			this.svuota();
			this.oggettoToccato = false;
			this.oggetto = oggettoRisposta(this.ultimoOggetto);
		},
		invia() {
			if (!this.pronto) return;
			this.$emit('send', {
				subject: this.oggetto.trim(),
				text: this.testo,
				attachments: this.allegati.filter(a => a.url && !a.errore).map(a => a.url),
			});
		},
	},
};
</script>

<style scoped>
.rc {
	border: 1px solid rgba(0, 0, 0, 0.12);
	border-radius: 10px;
	overflow: hidden;
	background: #fff;
	max-width: 900px;
	margin: 12px auto 0;
	transition: box-shadow 0.15s, border-color 0.15s;
}
.rc--drag {
	border-color: #01579b;
	box-shadow: 0 0 0 3px rgba(1, 87, 155, 0.15);
}
.rc-head {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 4px;
	padding: 8px 12px;
	background: #e6eff8;
	border-bottom: 1px solid #c9dbee;
	font-size: 13px;
}
.rc-head-title {
	font-weight: 600;
	color: #01579b;
}
.rc-head-email {
	color: #5f6b7a;
	word-break: break-all;
}
.rc-body {
	padding: 12px;
}
.rc-editor ::v-deep .ck-editor__editable {
	min-height: 140px;
	max-height: 360px;
}
.rc-editor ::v-deep .ck.ck-editor__main > .ck-editor__editable,
.rc-editor ::v-deep .ck.ck-toolbar {
	border-color: #d5dee9 !important;
}
.rc-editor ::v-deep .ck.ck-toolbar {
	background: #f7f9fc;
	border-radius: 6px 6px 0 0 !important;
}
.rc-editor ::v-deep .ck.ck-editor__main > .ck-editor__editable {
	border-radius: 0 0 6px 6px !important;
}
.rc-attachs {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
	margin-top: 10px;
}
.rc-chip {
	max-width: 240px;
}
.rc-chip-img {
	width: 20px;
	height: 20px;
	object-fit: cover;
	border-radius: 3px;
	margin: 0 6px 0 -4px;
}
.rc-chip-name {
	max-width: 180px;
	display: inline-block;
}
.rc-warning {
	margin-top: 6px;
	font-size: 12px;
	color: #c62828;
}
.rc-actions {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 6px;
	margin-top: 10px;
}
.rc-hint {
	font-size: 12px;
	color: #5f6b7a;
}

@media (max-width: 768px) {
	.rc {
		margin: 12px -4px 0;
	}
	.rc-body {
		padding: 8px;
	}
	.rc-hint {
		flex-basis: 100%;
		order: 5;
	}
	.rc-actions .v-btn:last-of-type {
		flex: 1 1 100%;
	}
}
</style>
